import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTransactionIdFromUrl } from '@/hooks/workflow/navigation/useTransactionIdFromUrl';
import {
  RefreshCw,
  ClipboardList,
  Settings,
  MessageSquare,
  BarChart3,
  Users,
  Webhook,
  BookOpen,
  Boxes,
  LifeBuoy,
  Bug,
} from 'lucide-react';
import { openDevPanel } from '@/stores/devPanelStore';
import { useIsAuthenticated } from '@/stores/authStore';
import { useUserProfile } from '@/services/user/hooks';
import { useCan } from '@/services/auth/useCan';
import { useTeamSettings } from '@/services/teams/hooks';
import { preloadRoute } from '@/pages/preloadRoutes';
import UserMenu from './UserMenu';
import CreditsDialog from '@/components/CreditsDialog';
import FeedbackDialog from '@/components/FeedbackDialog';
import { cn } from '@/lib/utils';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  description?: string;
}

const NavigationBar: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const userProfile = useUserProfile();
  const canViewAnalytics = useCan('analytics.view');
  const canViewTeamMembers = useCan('team.members.view');
  const canManageWebhooks = useCan('webhooks.manage');
  const canEditTeamSettings = useCan('team.settings.edit');
  const canEditAllTeamSettings = useCan('team.settings.editAll');
  const canManageModels = useCan('admin.models.manage');
  const canUseDevPanel = useCan('admin.dev-panel');

  const teamId = canEditTeamSettings ? userProfile.teamId || '' : '';
  const { data: teamSettings, isLoading: settingsLoading } = useTeamSettings(teamId);
  const showWebhooks =
    canManageWebhooks && (settingsLoading || (teamSettings?.webhookConfig?.enabled ?? false));

  const transactionId = useTransactionIdFromUrl();
  const location = useLocation();
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const navItems: NavItem[] = [
    {
      path: '/',
      label: 'Workflow',
      icon: RefreshCw,
      description: 'End-to-end document processing workflow',
    },
    {
      path: '/jobs',
      label: 'Jobs',
      icon: ClipboardList,
      description: 'View processing jobs and their status',
    },
    {
      path: '/docs',
      label: 'API Docs',
      icon: BookOpen,
      description: 'Interactive API reference for the Extract API',
    },
    {
      path: '/guide',
      label: 'Guide',
      icon: LifeBuoy,
      description: 'How to use the extractor',
    },
    ...(canViewAnalytics
      ? [
          {
            path: '/analytics',
            label: 'Analytics',
            icon: BarChart3,
            description: 'View usage metrics and analytics',
          },
        ]
      : []),
    ...(canViewTeamMembers
      ? [
          {
            path: '/team-members',
            label: 'Team Members',
            icon: Users,
            description: 'View and manage team members',
          },
        ]
      : []),
    ...(canManageModels
      ? [
          {
            path: '/model-registry',
            label: 'Model Registry',
            icon: Boxes,
            description: 'Manage extraction models (admin)',
          },
        ]
      : []),
    ...(showWebhooks
      ? [
          {
            path: '/webhooks',
            label: 'Webhooks',
            icon: Webhook,
            description: 'Monitor webhook deliveries',
          },
        ]
      : []),
    ...(canEditTeamSettings
      ? [
          {
            path: canEditAllTeamSettings
              ? '/team-settings?tab=hazcheck'
              : '/team-settings?tab=workflow-form',
            label: 'Team Settings',
            icon: Settings,
            description: 'Configure team Hazcheck and webhook settings',
          },
        ]
      : []),
  ];

  return (
    <>
      <nav className="flex items-center bg-card/80 glass border-b border-border/50 h-[52px] sticky top-0 z-[100] shadow-[0_1px_3px_0_rgb(0_0_0/0.04),0_1px_2px_-1px_rgb(0_0_0/0.04)]">
        <div className="flex items-center px-6 h-full border-r border-border/50 overflow-hidden">
          <img
            src="/hazcheck-extract.svg"
            alt="Hazcheck Extract"
            className="h-16 w-auto -my-2 dark:brightness-0 dark:invert"
          />
        </div>

        <div className="flex flex-1 h-full px-4 gap-1 items-center">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            const itemPathname = item.path.split('?')[0];
            const isActive =
              itemPathname === '/'
                ? location.pathname === '/' || !!transactionId
                : location.pathname === itemPathname;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'group flex items-center gap-2 px-3 py-2 bg-transparent border border-transparent rounded-lg text-muted-foreground no-underline cursor-pointer transition-all duration-200 text-sm font-medium h-9 relative',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20 hover:bg-primary/90'
                    : 'hover:bg-accent/80 hover:text-foreground',
                  'max-md:px-2 max-md:py-2'
                )}
                title={item.description}
                onMouseEnter={() => preloadRoute(item.path)}
                onFocus={() => preloadRoute(item.path)}
              >
                <IconComponent
                  size={16}
                  className={cn(
                    'shrink-0 transition-transform duration-200',
                    !isActive && 'group-hover:scale-110'
                  )}
                />
                <span className="text-[13px] max-md:hidden">{item.label}</span>
                {isActive && (
                  <span className="absolute -bottom-[7px] left-1/2 -translate-x-1/2 w-6 h-[2px] bg-primary rounded-full" />
                )}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3 px-6 h-full border-l border-border/50 max-md:px-4">
          {isAuthenticated && (
            <>
              {canUseDevPanel && (
                <button
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-transparent bg-transparent text-muted-foreground cursor-pointer transition-all duration-200 hover:bg-accent/80 hover:text-foreground active:scale-95"
                  onClick={openDevPanel}
                  title="Dev Panel (admin)"
                >
                  <Bug size={16} />
                </button>
              )}
              <button
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-transparent bg-transparent text-muted-foreground cursor-pointer transition-all duration-200 hover:bg-accent/80 hover:text-foreground active:scale-95"
                onClick={() => setShowFeedbackDialog(true)}
                title="Send Feedback"
              >
                <MessageSquare size={16} />
              </button>
              <UserMenu />
            </>
          )}
        </div>
      </nav>

      <CreditsDialog isOpen={showCreditsDialog} onClose={() => setShowCreditsDialog(false)} />

      <FeedbackDialog
        isOpen={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        transactionId={transactionId}
      />
    </>
  );
};

export default NavigationBar;
